import React, { lazy, Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from "date-fns";
import {
  setNestedParams,
  setFilterTag,
  getShipmentPlans,
  exportData
} from "redux/crm/shipment/shipmentPlanSlice";
import { AdvanceFilterDrawer, getEmptyValues, buildFilterParams, buildFilterTags, LoadingSpinner } from 'components/common';
import { SHIPMENT_PLAN_FILTER_FIELDS, SHIPMENT_PLAN_DIVIDERS } from 'components/app/crm/shipment/plan/shipmentPlanFilterConfig';
import ShipmentPlanFilterBar from 'components/app/crm/shipment/plan/ShipmentPlanFilterBar';
import { RiShip2Fill } from 'react-icons/ri';
import { FaArrowLeft, FaSyncAlt, FaFileExcel, FaTimes } from 'react-icons/fa';
import { PERPAGE_DEFAULT_SM } from 'app_config';
import { getCountries } from 'redux/settingsSlice';

const LazyShipmentPlan = lazy(() => import("../../../components/app/crm/shipment/plan/ShipmentPlan"));

const ENTRY_KEY = 'allData';
const EMPTY_RANGE = { startDate: '', endDate: '' };

const currentMonthRange = () => {
  const now = new Date();
  return {
    startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
    endDate:   format(endOfMonth(now),   'yyyy-MM-dd'),
  };
};

const buildParams = (filters, advanced) => ({
  ...(filters.etdRange?.startDate && filters.etdRange?.endDate && {
    etd_gteq: filters.etdRange.startDate,
    etd_lteq: filters.etdRange.endDate,
  }),
  ...(filters.search && { project_j_number_or_project_name_or_name_cont: filters.search }),
  ...(filters.status && { quick_filter: filters.status }),
  // Advanced filters come last so the drawer's own ETD range still wins, as before.
  ...advanced,
  page: 1,
  per_page: PERPAGE_DEFAULT_SM,
});

const ShipmentPlanPage = () => {
  const [, setSelected] = useState([]);
  const [filters, setFilters] = useState(() => ({
    search: '',
    etdRange: currentMonthRange(),
    status: '',
  }));
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  const { loading, allData } = useSelector((state) => state.crm_shipment_plan);
  const { countryData } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // filter fields derived from country data
  const filterFields = useMemo(() => {
    const countryOptions = (countryData || []).map((c) => ({ value: c.country, label: c.name }));
    return SHIPMENT_PLAN_FILTER_FIELDS(countryOptions);
  }, [countryData]);

  // Drawer values persist in page state — never reset on open/close. The applied
  // snapshot is separate so editing the drawer does not refetch until Apply.
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(SHIPMENT_PLAN_FILTER_FIELDS()));
  const [advancedParams, setAdvancedParams] = useState({});

  const activeFilterCount = allData?.filterTags?.length || 0;

  useEffect(() => {
    if (!countryData || countryData.length === 0) dispatch(getCountries());
    // Mount-only: re-checking on countryData changes would refetch forever if the country
    // list comes back empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Only the search box is typed into, so it is the only field that waits.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Keyed on the built params so a keystroke that has not settled yet does not refetch.
  const paramsKey = useMemo(
    () => JSON.stringify(buildParams({ ...filters, search: debouncedSearch }, advancedParams)),
    [filters, debouncedSearch, advancedParams]
  );

  useEffect(() => {
    const params = JSON.parse(paramsKey);
    dispatch(setNestedParams({ key: ENTRY_KEY, params }));
    dispatch(getShipmentPlans(params));
  }, [paramsKey, dispatch]);

  const handleFilterChange = useCallback(patch => setFilters(current => ({ ...current, ...patch })), []);

  const applyAdvanced = useCallback((values) => {
    dispatch(setFilterTag({ key: ENTRY_KEY, tags: buildFilterTags(filterFields, values) }));
    setAdvancedParams(buildFilterParams(filterFields, values));
  }, [dispatch, filterFields]);

  const handleFilterApply = useCallback((filterParams, tags) => {
    dispatch(setFilterTag({ key: ENTRY_KEY, tags }));
    setAdvancedParams(filterParams);
  }, [dispatch]);

  const handleAdvancedReset = useCallback(() => {
    const emptyValues = getEmptyValues(filterFields);
    setFilterValues(emptyValues);
    applyAdvanced(emptyValues);
  }, [filterFields, applyAdvanced]);

  const handleResetAll = useCallback(() => {
    setFilters({ search: '', etdRange: EMPTY_RANGE, status: '' });
    handleAdvancedReset();
  }, [handleAdvancedReset]);

  // Remove a single active filter (from the pill row) and re-apply the rest.
  const handleRemoveTag = useCallback((tag) => {
    const field = filterFields.find((f) => f.paramKey === tag.id || f.paramKeys?.[0] === tag.id);
    if (!field) return;

    const nextValues = { ...filterValues, [field.key]: getEmptyValues([field])[field.key] };
    setFilterValues(nextValues);
    applyAdvanced(nextValues);
  }, [filterFields, filterValues, applyAdvanced]);

  const handleReload = useCallback(() => {
    dispatch(getShipmentPlans(allData?.currentParams || {}));
  }, [dispatch, allData]);

  const handleDownload = useCallback(async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      await dispatch(exportData({ ...(allData?.currentParams || {}), export_status: 'all' }));
    } finally {
      setIsExporting(false);
    }
  }, [dispatch, allData, isExporting]);

  useEffect(() => { setPageReady(true); }, []);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Shipment Plan…</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* ── Header: title + Back / Reload / Export ── */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div
              className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0"
              style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }}
            />
            <div className="tw-flex tw-items-center tw-gap-2">
              <RiShip2Fill className="tw-text-sky-500" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">CSD Shipment Plan</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Track and manage shipment plans per period for the CSD department</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={() => navigate(-1)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer"
            >
              <FaArrowLeft size={12} /> Back
            </button>
            <button
              onClick={handleReload}
              disabled={loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} /> Reload
            </button>
            <button
              onClick={handleDownload}
              disabled={isExporting}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaFileExcel size={13} /> {isExporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <ShipmentPlanFilterBar
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleResetAll}
        onOpenAdvanced={() => setShowAdvanceFilter(true)}
        advancedCount={activeFilterCount}
      />

      {/* Active advanced-filter pills — each one removable on its own */}
      {activeFilterCount > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide">Filters:</span>
          {(allData?.filterTags || []).map((tag) => (
            <span
              key={tag.id}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-pl-2.5 tw-pr-1.5 tw-py-1 tw-bg-blue-50 tw-border tw-border-solid tw-border-blue-200 tw-text-blue-700 tw-rounded-xl tw-text-xs"
            >
              <span className="tw-font-semibold">{tag.name}:</span>
              <span className="tw-truncate tw-max-w-[160px]">
                {Array.isArray(tag.value) ? tag.value.join(', ') : String(tag.value)}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                title="Remove filter"
                className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-text-blue-400 hover:tw-text-white hover:tw-bg-blue-500 tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-transition-colors"
              >
                <FaTimes size={9} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Table card */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-overflow-hidden tw-relative">
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/70 tw-z-10">
            <LoadingSpinner size="md" />
          </div>
        )}
        <Suspense fallback={
          <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
            <LoadingSpinner size="md" />
          </div>
        }>
          <LazyShipmentPlan
            setSelected={setSelected}
            showAdvanceFilter={showAdvanceFilter}
            setShowAdvanceFilter={setShowAdvanceFilter}
          />
        </Suspense>
      </div>

      {/* Advanced filter drawer — lives at page level so values persist */}
      <AdvanceFilterDrawer
        show={showAdvanceFilter}
        onClose={() => setShowAdvanceFilter(false)}
        onApply={handleFilterApply}
        onReset={handleAdvancedReset}
        fields={filterFields}
        values={filterValues}
        onChange={(key, val) => setFilterValues((prev) => ({ ...prev, [key]: val }))}
        title="Advanced filters"
        description="Filter shipment plans by any combination of criteria below."
        dividerAfter={SHIPMENT_PLAN_DIVIDERS}
        width="460px"
      />
    </div>
  );
};

export default ShipmentPlanPage;
